import { useEffect, useMemo, useState } from "react";
import { searchAll } from "@/lib/api";

// Debounced (200ms) global search across all dates & types.
// Returns:
//   - query, setQuery: the controlled input value
//   - q: trimmed query
//   - results: flat array
//   - groupedResults: { task, habit, reminder, list }
//   - matchCounts: per-tab counts (null when q is empty)
//   - loading: boolean for "Searching…" state
//   - setResults: lets the host mutate the array (e.g. optimistic delete)
export function useGlobalSearch() {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const q = query.trim();

    useEffect(() => {
        if (!q) {
            setResults([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const id = setTimeout(async () => {
            try {
                const data = await searchAll(q);
                setResults(data.results || []);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 200);
        return () => clearTimeout(id);
    }, [q]);

    const groupedResults = useMemo(() => {
        const groups = { task: [], habit: [], reminder: [], list: [] };
        for (const r of results) {
            const k = r.kind === "list_item" ? "list" : r.kind;
            if (groups[k]) groups[k].push(r);
        }
        return groups;
    }, [results]);

    const matchCounts = q ? {
        task: groupedResults.task.length,
        habit: groupedResults.habit.length,
        reminder: groupedResults.reminder.length,
        list: groupedResults.list.length,
    } : null;

    const refetch = async () => {
        if (!q) return;
        try {
            const data = await searchAll(q);
            setResults(data.results || []);
        } catch { /* ignore */ }
    };

    return {
        query, setQuery, q,
        results, setResults,
        groupedResults, matchCounts,
        loading,
        refetch,
    };
}
